import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { isNil } from 'lodash-es';
import { SortDescriptor } from '@ds';
import { SavedFilterQueryState, usePageFiltersMemory } from './usePageFiltersMemory';

type QuerySyncState<F extends Record<string, unknown>> = {
  filters: F;
  page: number;
  itemsPerPage: number;
  searchText: string;
  sortBy?: SortDescriptor;
};

type UseFilterQueryParamsOptions<F extends Record<string, unknown>> = {
  defaultFilters: F;
  defaultItemsPerPage: number;
  defaultSortBy?: SortDescriptor;
  disabledKeys?: (keyof F)[];
};

const parseBoolean = (value: string): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return undefined;
};

const parseNilTemplateValue = (rawValue: string): unknown => {
  const booleanValue = parseBoolean(rawValue);

  if (booleanValue !== undefined) {
    return booleanValue;
  }

  if (rawValue.includes(',')) {
    return rawValue.split(',');
  }

  const parsedNumber = Number(rawValue);

  if (!Number.isNaN(parsedNumber) && String(parsedNumber) === rawValue) {
    return parsedNumber;
  }

  return rawValue;
};

const parseValueByTemplate = (rawValue: string, templateValue: unknown): unknown => {
  if (Array.isArray(templateValue)) {
    return rawValue ? rawValue.split(',') : [];
  }

  if (typeof templateValue === 'boolean') {
    return parseBoolean(rawValue);
  }

  if (typeof templateValue === 'number') {
    const parsedNumber = Number(rawValue);

    return Number.isNaN(parsedNumber) ? undefined : parsedNumber;
  }

  if (typeof templateValue === 'string') {
    return rawValue;
  }

  if (isNil(templateValue)) {
    return parseNilTemplateValue(rawValue);
  }

  return rawValue;
};

const serializeValue = (value: unknown): string | undefined => {
  if (isNil(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(String).join(',') : undefined;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    return trimmedValue || undefined;
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value);
  }

  return String(value);
};

export const hasSearchParams = (searchParams: URLSearchParams): boolean => {
  return Array.from(searchParams.keys()).length > 0;
};

const getPositiveNumberParam = (searchParams: URLSearchParams, key: string, fallbackValue: number): number => {
  const parsedValue = Number(searchParams.get(key));

  return !Number.isNaN(parsedValue) && parsedValue > 0 ? parsedValue : fallbackValue;
};

const getInitialSortByFromQuery = (
  searchParams: URLSearchParams,
  defaultSortBy?: SortDescriptor,
): SortDescriptor | undefined => {
  const column = searchParams.get('sortBy');
  const direction = searchParams.get('sortDirection');

  if (column && (direction === 'ascending' || direction === 'descending')) {
    return {
      column,
      direction,
    };
  }

  return defaultSortBy;
};

const getInitialFiltersFromQuery = <F extends Record<string, unknown>>(
  searchParams: URLSearchParams,
  defaultFilters: F,
  disabledKeys?: (keyof F)[],
): F => {
  const nextFilters = { ...defaultFilters };

  for (const key of Object.keys(defaultFilters) as (keyof F)[]) {
    const isDisabledKey = disabledKeys?.includes(key);

    if (!isDisabledKey) {
      const rawValue = searchParams.get(String(key));

      if (rawValue !== null) {
        nextFilters[key] = parseValueByTemplate(rawValue, defaultFilters[key]) as F[keyof F];
      }
    }
  }

  return nextFilters;
};

const getDefaultState = <F extends Record<string, unknown>>(
  defaultFilters: F,
  defaultItemsPerPage: number,
  defaultSortBy?: SortDescriptor,
): QuerySyncState<F> => {
  return {
    filters: defaultFilters,
    page: 1,
    itemsPerPage: defaultItemsPerPage,
    searchText: '',
    sortBy: defaultSortBy,
  };
};

const getSavedState = <F extends Record<string, unknown>>(
  defaultFilters: F,
  savedPageState: SavedFilterQueryState,
): QuerySyncState<F> => {
  return {
    filters: {
      ...defaultFilters,
      ...savedPageState.filters,
    } as F,
    page: savedPageState.page,
    itemsPerPage: savedPageState.itemsPerPage,
    searchText: savedPageState.searchText,
    sortBy: savedPageState.sortBy,
  };
};

const buildStateFromSearchParams = <F extends Record<string, unknown>>(
  searchParams: URLSearchParams,
  defaultFilters: F,
  defaultItemsPerPage: number,
  defaultSortBy?: SortDescriptor,
  disabledKeys?: (keyof F)[],
): QuerySyncState<F> => {
  return {
    filters: getInitialFiltersFromQuery(searchParams, defaultFilters, disabledKeys),
    page: getPositiveNumberParam(searchParams, 'page', 1),
    itemsPerPage: getPositiveNumberParam(searchParams, 'itemsPerPage', defaultItemsPerPage),
    searchText: searchParams.get('search') || '',
    sortBy: getInitialSortByFromQuery(searchParams, defaultSortBy),
  };
};

const appendFilterParams = <F extends Record<string, unknown>>(
  params: URLSearchParams,
  filters: F,
  disabledKeys?: (keyof F)[],
): void => {
  for (const key of Object.keys(filters) as (keyof F)[]) {
    const isDisabledKey = disabledKeys?.includes(key);

    if (!isDisabledKey) {
      const serializedValue = serializeValue(filters[key]);

      if (serializedValue !== undefined) {
        params.set(String(key), serializedValue);
      }
    }
  }
};

const appendSearchParam = (params: URLSearchParams, searchText: string): void => {
  const trimmedSearchText = searchText.trim();

  if (trimmedSearchText) {
    params.set('search', trimmedSearchText);
  }
};

const appendPaginationParams = (
  params: URLSearchParams,
  page: number,
  itemsPerPage: number,
  defaultItemsPerPage: number,
): void => {
  if (page > 1) {
    params.set('page', String(page));
  }

  if (itemsPerPage !== defaultItemsPerPage) {
    params.set('itemsPerPage', String(itemsPerPage));
  }
};

const appendSortParams = (params: URLSearchParams, sortBy?: SortDescriptor): void => {
  if (sortBy?.column) {
    params.set('sortBy', String(sortBy.column));
  }

  if (sortBy?.direction) {
    params.set('sortDirection', sortBy.direction);
  }
};

const buildQueryParams = <F extends Record<string, unknown>>(
  currentQueryParams: URLSearchParams,
  state: QuerySyncState<F>,
  defaultItemsPerPage: number,
  disabledKeys?: (keyof F)[],
): URLSearchParams => {
  const params = new URLSearchParams(currentQueryParams);

  appendFilterParams(params, state.filters, disabledKeys);
  appendSearchParam(params, state.searchText);
  appendPaginationParams(params, state.page, state.itemsPerPage, defaultItemsPerPage);
  appendSortParams(params, state.sortBy);

  return params;
};

export const useFilterQueryParams = <F extends Record<string, unknown>>({
  defaultFilters,
  defaultItemsPerPage,
  defaultSortBy,
  disabledKeys,
}: UseFilterQueryParamsOptions<F>) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const savedPageState = usePageFiltersMemory((store) => store.pages[location.pathname]);
  const setPageState = usePageFiltersMemory((store) => store.setPageState);

  const initialState = useMemo<QuerySyncState<F>>(() => {
    if (hasSearchParams(searchParams)) {
      return buildStateFromSearchParams(searchParams, defaultFilters, defaultItemsPerPage, defaultSortBy, disabledKeys);
    }

    if (savedPageState) {
      return getSavedState(defaultFilters, savedPageState);
    }

    return getDefaultState(defaultFilters, defaultItemsPerPage, defaultSortBy);
  }, [defaultFilters, defaultItemsPerPage, defaultSortBy, disabledKeys, savedPageState, searchParams]);

  const syncToQueryParams = useCallback(
    (state: QuerySyncState<F>) => {
      const nextParams = buildQueryParams(searchParams, state, defaultItemsPerPage, disabledKeys);

      const isParamsChanged = searchParams.toString() !== nextParams.toString();
      const params = isParamsChanged ? nextParams : searchParams;

      setSearchParams(params, { replace: true });

      setPageState(location.pathname, {
        filters: state.filters as Record<string, unknown>,
        page: state.page,
        itemsPerPage: state.itemsPerPage,
        searchText: state.searchText,
        sortBy: state.sortBy,
      });
    },
    [defaultItemsPerPage, disabledKeys, location.pathname, searchParams, setPageState, setSearchParams],
  );

  return {
    initialState,
    syncToQueryParams,
  };
};
